import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import _ from 'lodash'
import { yupResolver } from '@hookform/resolvers/yup'
import { useSideEffect } from '../../data/store/Effects/side-effects'
import { Enum } from '../../data/types/constants'
import { useAppSelector } from '../../data/store'
import { creditApplicationEffects, creditApplicationSelectors } from '../../data/store/CreditApplicationStore'
import { WorksheetSchema, Worksheet } from '../../data/schemas/PrequalificationSchema'
import Loader from '../components/Loader'
import { FormatCurrency } from '../utils/Formatter'
import CreditApplicationContainerStepProgressIcon from './CreditApplicationContainerStepProgressIcon'
import { CustomerFeedback } from '../../data/types/CustomerFeedback'

export default function WorksheetPage() {
  const { t } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()
  const dispatchEffect = useSideEffect()
  const isLoading = useAppSelector(creditApplicationSelectors.isLoading)
  const filteredCreditApp = useAppSelector(creditApplicationSelectors.getFilteredCreditApplication)
  const availableCredit = useAppSelector(creditApplicationSelectors.getAvailableCredit)
  const stepProgression = <CreditApplicationContainerStepProgressIcon currentStep={4} />
  const customerFeedback = useAppSelector(creditApplicationSelectors.getCustomerFeedback)

  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
  } = useForm<Worksheet>({
    mode: 'onBlur',
    defaultValues: WorksheetSchema.getDefault(),
    resolver: yupResolver(WorksheetSchema),
  })
  const computedInfo = useAppSelector(creditApplicationSelectors.getComputedInfo)
  const [hideInsuranceInfo, setHideInsuranceInfo] = React.useState<boolean>(true)

  React.useEffect(() => {
    if (filteredCreditApp == null) {
      navigate(`/creditApplication/${id as string}`)
    }
  }, [filteredCreditApp])

  React.useEffect(() => {
    if (id) dispatchEffect(creditApplicationEffects.getAvailbaleCredit(id))
  }, [filteredCreditApp])

  React.useEffect(() => {
    if (filteredCreditApp && availableCredit) {
      reset(
        _.omit<Worksheet>({
          ...WorksheetSchema.getDefault(),
          ...filteredCreditApp?.worksheet,
          term: filteredCreditApp.finalDecision.maxTermDuration,
          loanPurposeId: filteredCreditApp.loanPurposeId,
          finalDecisionMaxAmountFinanced: availableCredit.availableAmount,
          amountRequested: availableCredit.availableAmount,
        }),
      )
    }
  }, [filteredCreditApp, availableCredit])

  function onSubmit(data: Worksheet) {
    data.creditApplicationId = id as string

    dispatchEffect(creditApplicationEffects.createWorksheet(data)).then(() => {
      if (customerFeedback as CustomerFeedback) {
        navigate(`/creditApplication/${id as string}/feedback-page`)
      } else navigate(`/creditApplication/${id as string}/feedbackReview-page`)
    })
  }

  const generateTermOptions = useMemo(() => {
    const options = []
    if (filteredCreditApp) {
      const maxTerm = filteredCreditApp.finalDecision.maxTermDuration
      for (let i = 12; i <= maxTerm; i += 12) {
        options.push(
          <option key={i} value={i}>
            {t<string>('common.month', { days: i })}
          </option>,
        )
      }
    }
    return options
  }, [filteredCreditApp])

  const handleClick = () => {
    setHideInsuranceInfo(!hideInsuranceInfo)
  }

  const canAddInsurance =
    filteredCreditApp?.applicantProvince !== Enum.EProvince.quebec &&
    filteredCreditApp?.applicantProvince !== Enum.EProvince.saskatchewan

  return (
    <>
      {isLoading && <Loader title={t('common.withYouShortly')} description="" />}
      {!isLoading && (
        <main className="form-section">
          {stepProgression}
          <form onSubmit={handleSubmit(onSubmit)}>
            <h1>{t('worksheet.congratulations')}</h1>
            <div className="paragraph">
              <p>
                {t('worksheet.desiredAmount')} {canAddInsurance && t('worksheet.insurance')}
              </p>
            </div>

            <div className={`control-group  ${errors.amountRequested && 'error'}`}>
              <label>{t<string>('worksheet.loanAmount')}</label>
              <div className="number-wrap">
                <input type="text" {...register('amountRequested')} defaultValue={availableCredit?.availableAmount} />
              </div>
              {errors.amountRequested && (
                <div className="error-message">
                  {t<string>('worksheet.amountErrorMessage', {
                    maximumAmount: FormatCurrency(availableCredit?.availableAmount),
                  })}
                </div>
              )}
            </div>

            <div className="control-group">
              <label htmlFor="term">{t<string>('loan.term')}</label>
              <div className="dropdown-wrap">
                <select id="term" {...register('term')}>
                  {generateTermOptions}
                </select>
              </div>
            </div>

            <div className="control-group">
              <label>
                {t<string>('worksheet.monthlyPayment')}
                {FormatCurrency(computedInfo?.paymentForFrequency)}
              </label>
            </div>
            {canAddInsurance && (
              <>
                <button type="button" className="btn" onClick={handleClick}>
                  {t<string>('worksheet.includeInsurance')}
                  <i className="fa-solid fa-circle-info" style={{ color: '#0096ff' }} />
                </button>
                <div className="control-group" hidden={hideInsuranceInfo}>
                  <em>{t<string>('worksheet.tooltipSubtitle')}</em>
                  <br />
                  <br />
                  <li>{t<string>('worksheet.lifeInsuranceTitle')}</li>
                  <ul>{t<string>('worksheet.lifeInsuranceText')}</ul>
                  <br />
                  <li>{t<string>('worksheet.accidentalDisabilityInsuranceTitle')}</li>
                  <ul>{t<string>('worksheet.accidentalDisabilityInsuranceText')}</ul>
                </div>
                <div className="control-group">
                  <input {...register('includeInsurance')} id="includeInsurance" type="checkbox" />
                  <label htmlFor="includeInsurance">{t<string>('common.yes')}</label>
                </div>
              </>
            )}

            <button
              type="submit"
              className="btn btn-blue"
              disabled={isLoading || filteredCreditApp?.worksheet !== null}
            >
              <span>{t('common.submit')}</span>
              <i className="fa-regular fa-arrow-right" />
            </button>
          </form>
        </main>
      )}
    </>
  )
}
